import { Form } from 'antd';
import { DarwinFlex, DarwinSpin } from 'components/darwin-ui';
import { ContentHeader } from 'components/ui/layout/ContentHeader';
import { MetaDataProcessMap } from 'components/ui/processMap/MetaDataProcessMap';
import { ProcessMapFormMainSection } from 'components/ui/processMap/ProcessMapFormMainSection';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useUI } from 'contexts/UiContext';
import { parseISO } from 'date-fns';
import { useCallback, useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { CreateProcessMapPayload } from 'types/processesTypes';
import { extractQueryData } from 'utils/services';
import {
  useCreateProcessMap,
  useGetProcessMapById,
  useUpdateProcessMapByID,
} from 'api/services/processMapService';
import styles from './index.module.css';
import { ROUTES } from 'routes/routes';
import { ErrorType } from 'types/genericTypes';
import { HTTP_CREATED } from 'constants/backendResponses';
import { FORM_ACTIONS, FormAction } from 'constants/formActions';

type CreateProductFormProps = {
  formAction?: FormAction;
};

export type FormProcessMap = {
  name?: string;
  description?: string;
  finalProducts?: string[];
  createdBy?: string | Date;
  createdAt?: string | Date;
  updatedAt?: string | Date;
  externalCode?: number;
  eudrFile?: string[];
  processDocument?: string[];
};

const baseDefaultProductValues = {};

export const CreateProcessMapForm = ({
  formAction = FORM_ACTIONS.CREATE,
}: CreateProductFormProps) => {
  const { selectedCompany } = useUI();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const isCreateForm = formAction === FORM_ACTIONS.CREATE;
  const isEditForm = formAction === FORM_ACTIONS.EDIT;
  const isViewForm = formAction === FORM_ACTIONS.VIEW;
  const defaultValuesAdditionalProperties = !isCreateForm
    ? {
        createdBy: '',
        createdAt: '',
        updatedAt: '',
      }
    : {};

  //service for Create Process Map
  const {
    mutateAsync: createProcessMap,
    isPending: isCreatingProcessMapPending,
  } = useCreateProcessMap();

  //service for Update Process Map
  const {
    mutateAsync: updateProcessMap,
    isPending: isUpdatingProcessMapPending,
  } = useUpdateProcessMapByID();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isDirty },
    reset,
    watch,
  } = useForm<FormProcessMap>({
    defaultValues: {
      ...baseDefaultProductValues,
      ...defaultValuesAdditionalProperties,
    },
    mode: 'onChange',
  });

  const name = watch('name');

  const { id: processMapId, list: comeFromList } = useParams();
  const processMapByIdQuery = useGetProcessMapById(
    selectedCompany || '',
    processMapId || ''
  );

  const handleCancel = useCallback(() => {
    if (!!comeFromList) {
      return navigate(generatePath(ROUTES.PROCESSES.MAPS.LIST));
    }

    if (isEditForm) {
      return navigate(
        generatePath(ROUTES.PROCESSES.MAPS.MAP.VIEW, {
          id: processMapId || '',
        })
      );
    }

    navigate(ROUTES.PROCESSES.MAPS.LIST);
  }, [isEditForm, navigate, processMapId, comeFromList]);

  const handleNavigate = useCallback(
    (idNewProcess?: string) => {
      navigate(
        generatePath(ROUTES.PROCESSES.MAPS.MAP.VIEW, {
          id: processMapId || idNewProcess || '',
        })
      );
    },
    [navigate, processMapId]
  );

  // logic for view or edit data
  const processMapByIdData = extractQueryData(processMapByIdQuery, undefined);

  useEffect(() => {
    if (!isCreateForm && processMapByIdData && selectedCompany) {
      const {
        name,
        description,
        products,
        createdBy,
        createdAt,
        updatedAt,
        externalCode,
      } = processMapByIdData;

      const finalProducts = products?.map(p => p.finalProduct.id) || [];

      const viewOrEditPayload: FormProcessMap = {
        name,
        description,
        finalProducts,
        externalCode,
        ...(isViewForm || isEditForm
          ? {
              createdBy: createdBy
                ? `${createdBy.firstName} ${createdBy.lastName}`
                : '',
              createdAt: parseISO(createdAt),
              updatedAt: parseISO(updatedAt),
            }
          : {}),
      };

      reset(viewOrEditPayload);
    }
  }, [
    isCreateForm,
    isEditForm,
    isViewForm,
    processMapByIdData,
    reset,
    selectedCompany,
  ]);

  const handleCreateProcessMap = async (
    payload: CreateProcessMapPayload,
    saveAndExit: boolean
  ) => {
    try {
      const response = await createProcessMap({
        payload,
        companyId: selectedCompany || '',
      });
      openNotification(
        t('success'),
        t('process-map.create-success'),
        'success'
      );
      if (response.code === HTTP_CREATED && saveAndExit) {
        handleNavigate(response.data.id);
      }
      if (!saveAndExit) {
        navigate(
          generatePath(ROUTES.PROCESSES.MAPS.MAP.CONFIG, {
            id: response.data.id || '',
            list: null,
          })
        );
      }
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(t('error'), t('process-map.create-error'), 'error');
      }
    }
  };

  const handleEditProcessMap = async (
    payload: CreateProcessMapPayload,
    saveAndExit: boolean
  ) => {
    try {
      await updateProcessMap({
        payload,
        companyId: selectedCompany || '',
        traceabilityId: processMapId || '',
      });
      openNotification(
        t('success'),
        t('process-map.update-success'),
        'success'
      );
      if (saveAndExit) handleNavigate();
    } catch (err) {
      openNotification(t('error'), t('process-map.update-error'), 'error');
    }
  };

  // disable button and spinner logic
  const isSaveButtonDisabled =
    isViewForm || (isEditForm && !isDirty) || !isValid;

  const onSubmit =
    (saveAndExit: boolean): SubmitHandler<FormProcessMap> =>
    async data => {
      const { name, description, finalProducts, externalCode } = data;
      const payload: CreateProcessMapPayload = {
        name: name || '',
        description: description || '',
        finalProducts: finalProducts || [],
        externalCode,
      };

      isCreateForm
        ? handleCreateProcessMap(payload, saveAndExit)
        : handleEditProcessMap(payload, saveAndExit);
    };

  const isLoadingInitialDataOrSubmittingForm =
    processMapByIdQuery.isLoading ||
    isCreatingProcessMapPending ||
    isUpdatingProcessMapPending;

  const title = name
    ? name
    : isCreateForm
    ? t('process-map.new-process-map.title')
    : isEditForm
    ? t('process-map.title-edit')
    : t('process-map.title-view');

  return (
    <>
      <ContentHeader
        title={title}
        onSave={handleSubmit(onSubmit(false))}
        onCancel={handleCancel}
        saveDisabled={isSaveButtonDisabled}
        extraButtonDisabled={isSaveButtonDisabled}
        displayExtraButton={true}
        extraButtonTitle={t('save-exit')}
        onExtraButtonClick={handleSubmit(onSubmit(true))}
      />

      <DarwinSpin fullscreen spinning={isLoadingInitialDataOrSubmittingForm} />

      <DarwinFlex vertical className={styles.processMapFormRow}>
        <Form layout="vertical">
          <ProcessMapFormMainSection
            errors={errors}
            control={control}
            isViewForm={!!isViewForm}
            processMapByIdData={processMapByIdData}
            isCreateForm={isCreateForm}
          />
          {(isViewForm || isEditForm) && (
            <MetaDataProcessMap control={control} errors={errors} />
          )}
        </Form>
      </DarwinFlex>
    </>
  );
};
